import React, { FC, useEffect, useRef, useState } from 'react'
import { FormEditor } from '@bpmn-io/form-js'
import FileUploadModule from '../extensions/form/custom-file-render'
import FileDownloadModule from '../extensions/form/custom-file-download'
import CustomSelectModule from '../extensions/form/custom-http-bound-connector'
import DocumentScannerModule from '../extensions/form/custom-document-scanner'
import PropertiesPanelModule from '../extensions/form/custom-properties-http-bound-connector'
import schema from './empty.json'
import '@bpmn-io/form-js/dist/assets/form-js.css'
import '@bpmn-io/form-js/dist/assets/form-js-editor.css'
import '@bpmn-io/form-js/dist/assets/flatpickr/light.css'
import { Button, DeleteModal } from '@sistemiv/s-components'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import EditableInput from './EditableInput'
import { CheckIcon, ExclamationCircleIcon } from '@heroicons/react/24/outline'
import FilesAndDirectoriesService from '../../services/FilesAndDirectoriesService'
import { useDirectoryContent } from '../../repositories/directory/directoryContent.repository'
import { useProjectContent } from '../../repositories/project/projectContent.repository'
import Breadcrumbs from '../layout/Breadcrumbs'
import { useRenameItem } from '../../repositories/project-item/mutations/item-rename'
import FormPreviewModal from '../modals/FormPreviewModal'

type Props = {
  file: string
  directoryPath: string
  //onSave: (xml: any) => void
}

const FormModeler: FC<Props> = ({ file, directoryPath }) => {
  const formRef = useRef<HTMLDivElement>(null)
  //const fileInputRef = useRef<HTMLInputElement>(null)
  const { org, project } = useParams()
  const modelerRef = useRef<any>(null)
  const [canUndo, setCanUndo] = useState(false)
  const [canRedo, setCanRedo] = useState(false)
  const { t } = useTranslation()
  const [fileName, setFileName] = useState(file && file !== 'new' ? file : 'New Form diagram.form')
  const [saved, setSaved] = useState(false)
  const [failed, setFailed] = useState(false)
  const { data: folderFiles } = useDirectoryContent(directoryPath)
  const { data: projectFiles } = useProjectContent()
  const [openWarningModal, setOpenWarningModal] = useState(false)
  // const [inputFocused, setInputFocused] = useState(false)
  // const hasBeenSaved = useRef<boolean>(!!(file && file !== 'new'))
  const [saveLoading, setSaveloading] = useState(false)

  const { mutate: renameItem } = useRenameItem(directoryPath)

  const [openPreviewModal, setOpenPreviewModal] = useState(false)
  const [previewSchema, setPreviewSchema] = useState<any>(null)

  // Autosave funkcija
  // const saveToLocalStorage = useCallback(() => {
  //   if (!modelerRef.current) return

  //   try {
  //     const schema = modelerRef.current.saveSchema()
  //     localStorage.setItem(AUTOSAVE_KEY, JSON.stringify(schema))
  //   } catch (error) {
  //     console.error('Error saving schema:', error)
  //   }
  // }, [])

  useEffect(() => {
    if (!formRef.current) return

    modelerRef.current = new FormEditor({
      container: formRef.current,
      additionalModules: [
        FileUploadModule,
        FileDownloadModule,
        CustomSelectModule,
        DocumentScannerModule,
        PropertiesPanelModule,
      ],
    })

    const loadSchema = async () => {
      // const savedSchema = localStorage.getItem(AUTOSAVE_KEY)
      // const initialSchema = savedSchema ? JSON.parse(savedSchema) : schema

      try {
        await modelerRef.current.importSchema(schema)
      } catch (error) {
        console.error('Error loading schema:', error)
      }
    }

    loadSchema()

    // Pristup commandStack-u
    const commandStack = modelerRef.current.get('commandStack')
    const eventBus = modelerRef.current.get('eventBus')

    if (commandStack && eventBus) {
      // Dodajemo event listener na undo/redo preko eventBus-a
      eventBus.on('commandStack.changed', () => {
        setCanUndo(commandStack.canUndo())
        setCanRedo(commandStack.canRedo())
        //saveToLocalStorage()
      })
    } else {
      console.warn('commandStack or eventBus is not available in FormEditor')
    }

    return () => {
      modelerRef.current?.destroy()
    }
  }, [])

  useEffect(() => {
    const fetchAndLoadDiagram = async () => {
      try {
        const response = await FilesAndDirectoriesService.download(
          org!,
          `${project}/${directoryPath ? `${directoryPath}/${file}` : `${file}`}`,
          'files',
        )
        const contentType = response.headers['content-type']
        // Proveri da li je uspešno vraćen Blob
        // if (response.ok) {
        const blob = response.data instanceof Blob ? response.data : new Blob([response.data], { type: contentType })
        const text = await blob.text() // Pretvoriš Blob u tekst (XML)
        const formData = JSON.parse(text)
        await modelerRef.current.importSchema(formData)
        // }
      } catch (error) {
        console.error('Greška pri učitavanju dijagrama:', error)
      }
    }

    if (file && file !== 'new' && modelerRef.current) {
      fetchAndLoadDiagram()
    }
  }, [file, org, directoryPath, project])

  const handleExport = () => {
    if (!modelerRef.current) return
    const schema = modelerRef.current.saveSchema()
    const jsonStr = JSON.stringify(schema)
    const blob = new Blob([jsonStr], { type: 'application/octet-stream' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `${fileName}${!fileName.includes('.form') ? '.form' : ''}`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }

  const handlePreview = () => {
    if (!modelerRef.current) return
    const schema = modelerRef.current.saveSchema()
    setPreviewSchema(schema)

    setOpenPreviewModal(true)
  }

  // const handleUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
  //   const file = event.target.files?.[0]
  //   if (!file) return

  //   try {
  //     const arrayBuffer = await file.arrayBuffer()
  //     const text = new TextDecoder('utf-8').decode(arrayBuffer)
  //     const formJSON = JSON.parse(text)
  //     await modelerRef.current.importSchema(formJSON)
  //     localStorage.setItem(AUTOSAVE_KEY, JSON.stringify(formJSON))
  //   } catch (error) {
  //     console.error('Error reading the file:', error)
  //     alert('Failed to load the form. Please upload a valid JSON file.')
  //   }
  // }

  const handleUndo = () => {
    const commandStack = modelerRef.current?.get('commandStack')
    if (commandStack && commandStack.canUndo()) {
      commandStack.undo()
    }
  }

  const handleRedo = () => {
    const commandStack = modelerRef.current?.get('commandStack')
    if (commandStack && commandStack.canRedo()) {
      commandStack.redo()
    }
  }
  const onSave = async () => {
    setSaveloading(true)
    try {
      const schema = modelerRef.current.saveSchema()
      const jsonStr = JSON.stringify(schema)
      const blob = new Blob([jsonStr], { type: 'application/json' })
      const file = new File([blob], `${fileName}${!fileName.includes('.form') ? '.form' : ''}`, {
        type: 'application/json',
      })

      await FilesAndDirectoriesService.saveFile(
        org!,
        `${project}/${
          directoryPath
            ? `${directoryPath}/${fileName}${!fileName.includes('.form') ? '.form' : ''}`
            : `${fileName}${!fileName.includes('.form') ? '.form' : ''}`
        }`,
        file,
      )
      //alert('File saved successfully!')
      setSaved(true)
      setTimeout(() => {
        setSaved(false)
      }, 1000)
    } catch (error) {
      console.error('Error saving file:', error)
      // alert('Failed to save file.')
      setFailed(true)
      setTimeout(() => {
        setFailed(false)
      }, 1000)
    } finally {
      setSaveloading(false)
      if (openWarningModal) setOpenWarningModal(false)
    }
  }

  // const handleRename = (newName: string) => {
  //   // if (!hasBeenSaved.current) {
  //   //   setFileName(newName)
  //   //   return
  //   // }
  //   // if (newName === fileName) return
  //   renameItem(
  //     {
  //       organization: org!,
  //       path: `${project}/${directoryPath ? `${directoryPath}/` : ''}${fileName}`,
  //       newName,
  //       type: 'files',
  //     },
  //     {
  //       onSuccess: () => setFileName(newName),
  //       onError: () => {
  //         setFailed(true)
  //         setTimeout(() => {
  //           setFailed(false)
  //         }, 1000)
  //       },
  //     },
  //   )
  // }

  const handleRename = (newName: string) => {
    return new Promise<void>((resolve, reject) => {
      renameItem(
        {
          organization: org!,
          path: `${project}/${directoryPath ? `${directoryPath}/` : ''}${file}`,
          newName,
          type: 'files',
        },
        {
          onSuccess: () => {
            setFileName(newName)
            resolve()
          },
          onError: () => {
            setFailed(true)
            setTimeout(() => setFailed(false), 1000)
            reject()
          },
        },
      )
    })
  }

  // const handleSave = async () => {
  //   if (!modelerRef.current) return
  //   const files = directoryPath ? folderFiles : projectFiles
  //   if (
  //     (file === 'new' || fileName !== file) &&
  //     // !hasBeenSaved &&
  //     files.contents.find((file) =>
  //       fileName.includes('.') ? file.name === fileName : file.name.slice(0, file.name.lastIndexOf('.')) === fileName,
  //     )
  //   ) {
  //     setOpenWarningModal(true)
  //   } else {
  //     if (file && file !== 'new' && file !== fileName) {
  //       handleRename(fileName)
  //     }
  //     onSave()
  //   }
  // }

  const handleSave = async () => {
    if (!modelerRef.current) return
    const files = directoryPath ? folderFiles : projectFiles

    const fileExists = files.contents.find((file) =>
      fileName.includes('.') ? file.name === fileName : file.name.slice(0, file.name.lastIndexOf('.')) === fileName,
    )

    if (file === 'new' && fileExists) {
      setOpenWarningModal(true)
    } else {
      try {
        if (file && file !== 'new' && file !== fileName) {
          await handleRename(fileName)
        }
        await onSave()
      } catch (err) {
        console.error('Rename or save failed:', err)
      }
    }
  }

  return (
    <div className='w-full h-full flex flex-col'>
      <div className='w-full h-16 flex justify-between border-b border-gray-300 items-center px-8'>
        <Breadcrumbs>
          {/* <EditableInput value={fileName} onRename={handleRename} setInputFocused={setInputFocused} /> */}
          {/* <EditableInput value={fileName} onRename={handleRename} setInputFocused={setInputFocused} /> */}
          <EditableInput value={fileName} onChange={setFileName} />
        </Breadcrumbs>
        <div className='flex gap-x-4'>
          {failed && (
            <div className='flex items-center justify-center text-red-600 transition-opacity duration-500 ease-in-out opacity-100'>
              <ExclamationCircleIcon className='text-red-600 w-6 h-6 mr-2' /> <span>Failed to save</span>
            </div>
          )}
          {saved && !failed ? (
            <div className='flex items-center justify-center text-[#4B526C] transition-opacity duration-500 ease-in-out opacity-100'>
              <CheckIcon className='text-[#4B526C] w-6 h-6 mr-2' /> <span>Saved</span>
            </div>
          ) : (
            <Button
              className='border border-blue-500 text-blue-500 bg-white px-4 !py-1 rounded-md hover:bg-sky-100  transition-opacity duration-500 ease-in-out opacity-100 disabled:opacity-50 disabled:cursor-not-allowed'
              onClick={handleSave}
              // disabled={inputFocused}
            >
              {t('save')}
            </Button>
          )}
          <Button
            className='border border-blue-500 text-blue-500 bg-white  px-4 !py-1 rounded-md hover:bg-sky-100 transition-colors duration-300'
            onClick={handleExport}
          >
            {t('download')}
          </Button>
          <Button
            className='border border-blue-500 text-blue-500 bg-white  px-4 !py-1 rounded-md hover:bg-sky-100 transition-colors duration-300'
            onClick={handlePreview}
          >
            {t('preview')}
          </Button>
        </div>
      </div>
      <div className='w-full h-full overflow-auto'>
        <div id='form' className='h-full' ref={formRef}></div>
      </div>
      <div className='h-16 border border-solid border-slate-300 py-4 px-8 flex items-center justify-between space-x-5'>
        <div className='flex items-center px-8 gap-x-5'>
          <Button
            className='border border-gray-500 text-gray-500 bg-white px-4 py-1 rounded hover:bg-gray-100 transition-colors duration-300'
            onClick={handleUndo}
            disabled={!canUndo}
          >
            Undo
          </Button>
          <Button
            className='border border-gray-500 text-gray-500 bg-white px-4 py-1 rounded hover:bg-gray-100 transition-colors duration-300'
            onClick={handleRedo}
            disabled={!canRedo}
          >
            Redo
          </Button>
        </div>
      </div>
      {openWarningModal && (
        <DeleteModal
          title={t('warning')}
          description={t('saveFileWarningMsg')}
          open={openWarningModal}
          setOpen={setOpenWarningModal}
          onDeleteConfirmed={onSave}
          actionLabel={t('continue')}
          isLoading={saveLoading}
        />
      )}
      {openPreviewModal && (
        <FormPreviewModal
          open={openPreviewModal}
          setOpen={setOpenPreviewModal}
          schema={previewSchema}
          readOnly={false}
        />
      )}
    </div>
  )
}

export default FormModeler
